/**
 * useCompareToThrive — drives the Compare dry-run lifecycle (subtask 06).
 *
 * 202 → poll → terminal, against the shared typed `ImportsClient` (which runs
 * against MSW today, the live API after subtask 08 — no call-site change). On
 * mount/`start` it POSTs `/compare`, then polls `GET /compare` until the run is
 * terminal (`succeeded` / `failed`).
 *
 * Returns the latest `CompareResult`, an `isRunning` flag, and any error so the
 * modal can render the running / terminal / error states. Polling stops on
 * terminal status and on unmount.
 */

import type { CompareResult } from "@nu-assist/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ImportsClient } from "../../clients/imports.client";

const TERMINAL = new Set(["succeeded", "failed"]);
const DEFAULT_POLL_MS = 1500;

export interface UseCompareToThriveOptions {
  client: ImportsClient;
  importId: string;
  /** Poll interval in ms (tests pass a small value). */
  pollIntervalMs?: number;
  /** Restrict the dry-run to a selection (optional). */
  contactIds?: string[];
  /** Auto-start on mount (default true). */
  autoStart?: boolean;
}

export interface UseCompareToThriveResult {
  result: CompareResult | null;
  isRunning: boolean;
  error: Error | null;
  /** Manually (re)start a compare run. */
  start: () => Promise<void>;
}

export function useCompareToThrive({
  client,
  importId,
  pollIntervalMs = DEFAULT_POLL_MS,
  contactIds,
  autoStart = true,
}: UseCompareToThriveOptions): UseCompareToThriveResult {
  const [result, setResult] = useState<CompareResult | null>(null);
  const [isRunning, setIsRunning] = useState<boolean>(autoStart);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);
  // Guards against a duplicate POST when `start` is called twice in the same tick
  // — React StrictMode (dev) double-invokes the mount effect (mount → cleanup →
  // mount) synchronously, which used to enqueue TWO /compare runs per action.
  const startInFlightRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    try {
      const next = await client.getCompare(importId);
      if (cancelledRef.current) return;
      setResult(next);
      if (TERMINAL.has(next.status)) {
        setIsRunning(false);
        clearTimer();
        return;
      }
      timerRef.current = setTimeout(() => void poll(), pollIntervalMs);
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setIsRunning(false);
      clearTimer();
    }
  }, [client, importId, pollIntervalMs, clearTimer]);

  const start = useCallback(async () => {
    // Collapse a synchronous double-call to a single POST (see startInFlightRef).
    // Set BEFORE the first await so the second same-tick call is dropped; cleared
    // once the run settles so a later explicit re-run still works.
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    setError(null);
    setIsRunning(true);
    setResult(null);
    try {
      await client.startCompare(
        importId,
        contactIds ? { contact_ids: contactIds } : undefined,
      );
      await poll();
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e instanceof Error ? e : new Error(String(e)));
      setIsRunning(false);
    } finally {
      startInFlightRef.current = false;
    }
  }, [client, importId, contactIds, poll]);

  // Run-once-on-mount lifecycle: kick the initial compare (when `autoStart`)
  // and always tear down the poll timer on unmount. `start` / `autoStart` /
  // `clearTimer` are intentionally NOT in the dep array — re-running this effect
  // on every render would restart the dry-run in a loop. The `cancelledRef`
  // guard makes the async `start` unmount-safe.
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once lifecycle; see comment.
  useEffect(() => {
    cancelledRef.current = false;
    if (autoStart) {
      void start();
    }
    return () => {
      cancelledRef.current = true;
      clearTimer();
    };
  }, []);

  return { result, isRunning, error, start };
}
